import React, { useEffect, useState } from "react";
import { Box, Flex, Link } from "theme-ui";

import H5 from "components/typography/H5";
import P from "components/typography/P";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";

import { getSitePlans, getSiteHistory } from "api/ServicePlans";

/**
 * MASTER_PLAN.md §3.9 #20 (mockup 09) — Site Plan Activity panel on
 * Location Detail. Appears only "when the viewing user is inside a
 * plan context and the Location has an LCP for the plan's client"
 * (§2.2 coexistence table) — approximated here as: render one section
 * per LCP-at-this-location that has at least one attached Service
 * Plan. A Location with zero plan-tagged profiles renders nothing,
 * so the ~6,007 legacy / non-plan locations see no change.
 *
 * Scoped down from mockup 09, not faked:
 *   - Polygon map embed ("Site polygons" panel) — needs the mulch
 *     calculator's zone-rendering component wired to a specific
 *     LCP/client context; existing SiteMapSection is record-scoped
 *     (Quote/Invoice/PO/Project), not Location+client-scoped. Follow-up.
 *   - "Next up" cadence card — needs Phase 2 cadence data. Open
 *     decision #7: status vocabulary beyond raw last-serviced dates
 *     waits for Phase 2, so no "Due September" style badge is invented
 *     here.
 *   - Per-service-type "Current" pill — same reason; shows last-serviced
 *     date only, no due/overdue computation without cadence data.
 *
 * Shares its dot-timeline visual language with SiteHistoryModal
 * (Clients/components, §3.9 #21) — same green/amber/grey legacy
 * vocabulary, deliberately not extracted into a shared component yet
 * since the two call different endpoints for different audiences
 * (LCP-scoped vs modal-triggered-from-plan-context) and the render
 * logic is small enough that duplication is cheaper than a premature
 * shared abstraction.
 */
export default function SitePlanActivityPanel({
  profiles,
}: {
  profiles: Array<{ id: string; clientName: string | null }>;
}) {
  const [sections, setSections] = useState<
    Array<{
      lcpId: string;
      clientName: string | null;
      planNames: string[];
      plans: Array<{ id: string | null; name: string | null }>;
      events: any[];
    }>
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const results = await Promise.all(
        (profiles ?? []).map(async (profile) => {
          const plans = await getSitePlans({ data: { lcpId: profile.id } });
          if (!Array.isArray(plans) || plans.length === 0) return null;
          const events = await getSiteHistory({ data: { lcpId: profile.id } });
          return {
            lcpId: profile.id,
            clientName: profile.clientName,
            planNames: plans.map((p: any) => p.servicePlan?.name).filter(Boolean),
            // SESSION 46 GAP-18 — carry the plan id/name pairs so the
            // section header can link to Plan Detail (mockup 09
            // "View plan"). Falls back to name-only display when a plan
            // has no id (shouldn't happen, but keeps the render safe).
            plans: plans
              .map((p: any) => ({
                id: p.servicePlan?.id || null,
                name: p.servicePlan?.name || null,
              }))
              .filter((p: any) => p.name),
            events: Array.isArray(events) ? events : [],
          };
        }),
      );
      if (!cancelled) {
        setSections(results.filter(Boolean) as any);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profiles]);

  if (loading || sections.length === 0) return null;

  return (
    <Box sx={{ marginTop: "24px" }}>
      {sections.map((section) => {
        const lifetimeYards = section.events.reduce((sum, e) => sum + (Number(e.yards) || 0), 0);
        const byType: Record<string, any> = {};
        for (const e of section.events) {
          if (!byType[e.serviceType] || new Date(e.date) > new Date(byType[e.serviceType].date)) {
            byType[e.serviceType] = e;
          }
        }

        return (
          <Box
            key={section.lcpId}
            sx={{
              border: "1px solid",
              borderColor: "inputOutline",
              borderRadius: "8px",
              overflow: "hidden",
              background: "white",
              marginBottom: "16px",
            }}
          >
            <Box sx={{ padding: "16px 20px", background: "#F1EFE8", borderBottom: "1px solid", borderColor: "inputOutline" }}>
              <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
                <Box sx={{ minWidth: 0 }}>
                  <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"}>
                    Plan Activity
                  </H5>
                  <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} marginTop={"2px"}>
                    {section.clientName ? `${section.clientName} · ` : ""}
                    {section.planNames.length ? section.planNames.join(", ") : "Plan-tagged site"}
                  </Subtext>
                </Box>
                {/* SESSION 46 GAP-18 — "View plan" cross-link (mockup 09).
                 * Links to the first plan carrying an id; the panel is a
                 * hub, not a dead end. */}
                {section.plans[0]?.id && (
                  <Link
                    href={`/maintenance/plans/${section.plans[0].id}`}
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: themevals.colors.info,
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View plan →
                  </Link>
                )}
              </Flex>
            </Box>

            <Box sx={{ padding: "16px 20px" }}>
              <Flex sx={{ gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
                {Object.entries(byType).map(([type, e]: [string, any]) => (
                  <Box key={type} sx={{ background: "#F7F5EE", borderRadius: "8px", padding: "10px 14px", minWidth: "180px" }}>
                    <Subtext fontSize={"10px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
                      {formatServiceType(type).toUpperCase()}
                    </Subtext>
                    <P fontFamily={"Poppins"} fontSize={"13px"} margin={0} marginTop={"4px"}>
                      last {new Date(e.date).toLocaleDateString()}
                    </P>
                  </Box>
                ))}
                <Box sx={{ background: "#F7F5EE", borderRadius: "8px", padding: "10px 14px", minWidth: "160px" }}>
                  <Subtext fontSize={"10px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
                    LIFETIME YARDS
                  </Subtext>
                  <P fontFamily={"Poppins"} fontSize={"18px"} fontWeight={600} margin={0} marginTop={"2px"}>
                    {lifetimeYards}
                  </P>
                  <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0}>
                    across {section.events.length} event{section.events.length === 1 ? "" : "s"}
                  </Subtext>
                </Box>
              </Flex>

              {section.events.length > 0 && (
                <Box sx={{ position: "relative", paddingLeft: "18px" }}>
                  <Box sx={{ position: "absolute", left: "5px", top: "6px", bottom: "6px", width: "1px", background: "inputOutline" }} />
                  {section.events.map((e: any, idx: number) => (
                    <Flex key={e.id ?? idx} sx={{ position: "relative", marginBottom: idx === section.events.length - 1 ? 0 : "14px" }}>
                      <Box
                        sx={{
                          position: "absolute",
                          left: "-18px",
                          top: "4px",
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          background: dotColor(e.serviceType, e.legacyPill),
                        }}
                      />
                      <Box>
                        <Flex sx={{ alignItems: "center", gap: "8px" }}>
                          <P
                            fontFamily={"Poppins"}
                            fontSize={"13px"}
                            fontWeight={500}
                            margin={0}
                            color={e.legacyPill ? themevals.colors.dkgrey : themevals.colors.black}
                          >
                            {formatServiceType(e.serviceType)}
                            {e.yards ? ` · ${e.yards} yd³` : ""}
                          </P>
                          {e.legacyPill && (
                            <Box sx={{ background: "#EEEBDE", borderRadius: "8px", padding: "1px 8px" }}>
                              <Subtext fontSize={"10px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
                                Legacy
                              </Subtext>
                            </Box>
                          )}
                        </Flex>
                        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"2px"}>
                          {e.date ? new Date(e.date).toLocaleDateString() : ""}
                          {e.depth ? ` · ${e.depth}" depth` : ""}
                        </Subtext>
                        {/* SESSION 46 GAP-19 — provenance line:
                         * provider name + source project number
                         * (mockup 09 `Southeast Bulk · P-2026-00234`).
                         * Only rendered for completion-written events
                         * that carry them; legacy backfill rows omit it. */}
                        {(e.provider?.name || e.sourceProject?.sequence || e.product) && (
                          <Subtext
                            fontSize={"11px"}
                            fontFamily={"Poppins"}
                            margin={0}
                            marginTop={"1px"}
                            color={themevals.colors.dkgrey}
                          >
                            {[
                              e.product || null,
                              e.provider?.name || null,
                              e.sourceProject?.sequence
                                ? `P-${String(e.sourceProject.sequence).padStart(5, "0")}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Subtext>
                        )}
                      </Box>
                    </Flex>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function dotColor(type: string, legacyPill: boolean): string {
  if (legacyPill) return "#B8B4A5";
  if (type === "dig_out") return "#633806";
  return "#27500A";
}

function formatServiceType(type: string): string {
  switch (type) {
    case "mulched":
      return "Mulched";
    case "dig_out":
      return "Dig-out";
    case "inspection":
      return "Inspection";
    case "note":
      return "Note";
    default:
      return type || "Service";
  }
}
